import React, { Suspense } from 'react';
import { Image } from '@nile/components/src/Components/Nodata';
import envConfig from '@nile/home/src/Config/env';
import { TextStyle } from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { cloneDeep, isEmpty } from 'lodash';
import { getTranslatedText } from '@nile/components';
import { changeTimeFormat } from '@nile/reports/src/modules/EfficiencyReport/util';
import { NoDataCardWrapper } from './styles';
import { EFFICIENCY_REPORT } from '../../../../home/constants';
const ReactECharts = React.lazy(() => import('echarts-for-react'));

const EChart = (props) => {
  const intl = useIntl();
  const LABEL_WIDTH = 0.25;
  const swapAxisWidget = [
    'TOP_TECH_TIME_SPENT_EFFICIENCY',
    'TOP_CLIENT_TIME_SPENT_EFFICIENCY'
  ];
  const {
    showLoading = false,
    internalName = null,
    reportType = null,
    chartType = null,
    isEfficiencyReport = false
  } = props;
  if (isEmpty(props?.chartOptions)) {
    return (
      <NoDataCardWrapper className="flex-center full-width fcol">
        <div className="flex-center">
          <Image
            url={`url(${envConfig.imageRoot}svg/chartEmpty.svg)`}
            height={isEfficiencyReport ? '90px' : '150px'}
            width={isEfficiencyReport ? '120px' : '200px'}
          />
        </div>
        <div className="flex-center mt10">
          <TextStyle variation="subdued">
            <FormattedMessage id="reports.chart.data.empty" />
          </TextStyle>
        </div>
      </NoDataCardWrapper>
    );
  }
  const getGridOptions = (formattedOptions) => {
    if (formattedOptions.grid) {
      return formattedOptions.grid;
    }
    return {
      left: '8%',
      right: '25%'
    };
  };

  const getFormattedOptions = (opts) => {
    const formattedOptions = opts;

    if (formattedOptions?.xAxis && formattedOptions?.xAxis?.axisLabel) {
      formattedOptions.xAxis.axisLabel.width = props.opts.height * LABEL_WIDTH;
    }
    formattedOptions.grid = getGridOptions(formattedOptions);
    if (!formattedOptions.series) {
      formattedOptions.series = [{}];
    }

    if (
      internalName &&
      props?.chartOptions?.isHorizontal &&
      swapAxisWidget.includes(internalName)
    ) {
      formattedOptions.yAxis = {
        ...formattedOptions?.yAxis,
        inverse: true
      };
    }

    formattedOptions.legend = formattedOptions.legend || {
      textStyle: {
        overflow: 'break',
        width: '100'
      },
      right: 0,
      top: '10%',
      type: 'scroll',
      orient: 'vertical'
    };

    if (formattedOptions?.series?.[0]?.type === 'pie') {
      delete formattedOptions.legend;
    }

    if (reportType === EFFICIENCY_REPORT) {
      if (chartType !== 'MULTIPLE') {
        formattedOptions.legend =
          chartType !== 'MULTIPLE'
            ? { show: false }
            : { top: '5%', left: 'center' };
      }

      const topPosition = chartType !== 'MULTIPLE' ? { top: '5%' } : {};

      formattedOptions.grid = {
        left: '5%',
        right: '5%',
        ...topPosition
      };
      formattedOptions.xAxis = {
        ...formattedOptions?.xAxis,
        axisLabel: {
          ...formattedOptions?.xAxis?.axisLabel,
          width: 80,
          rotate: 25
        }
      };

      const getValueFormat = (series) => {
        const metricUnitType = props?.chart?.metrics?.find(
          (el) => el?.attribute === series?.seriesId
        )?.metricUnit;
        const isTimeSeries = ['HOURS', 'DAYS'].includes(metricUnitType);

        if (isTimeSeries) {
          const { days, hours, minutes } = changeTimeFormat(series?.value);
          return series?.value
            ? `${(days && `${days}d`) || ''} ${(hours && `${hours}h`) ||
                ''} ${(minutes && `${minutes}m`) || ''}`
            : getTranslatedText(intl, 'app.noData.message');
        }
        return series?.value;
      };

      formattedOptions.tooltip = {
        ...formattedOptions?.tooltip,
        valueFormatter: (value) => {
          const { days, hours, minutes } = changeTimeFormat(value);
          return value
            ? `${(days && `${days}d`) || ''} ${(hours && `${hours}h`) ||
                ''} ${(minutes && `${minutes}m`) || ''}`
            : getTranslatedText(intl, 'app.noData.message');
        }
      };
      if (chartType === 'MULTIPLE') {
        formattedOptions.tooltip = {
          ...formattedOptions?.tooltip,
          formatter: (params) => {
            return `
                <div>
                <div style="font-weight:600">${params?.[0]?.axisValue}</div>
                ${params
                  .map(
                    (param) =>
                      `<div>
                        ${param?.marker}
                        <span style="font-weight:600">${param?.seriesName}<span>
                        <span>${getValueFormat(param)}</span>
                       </div>`
                  )
                  .join('')}
                </div>
              `;
          }
        };
      }
    }

    return formattedOptions;
  };

  return (
    <Suspense fallback={<i>loading...</i>}>
      <ReactECharts
        option={getFormattedOptions(props?.chartOptions)}
        opts={props?.opts}
        notMerge
        lazyUpdate
        style={{ height: props?.opts?.height }}
        showLoading={showLoading}
      />
    </Suspense>
  );
};

export default EChart;
